const CAMERA_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(14,39,39,0.45)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>';

function renderPhotoCredit(credit: string, creditUrl: string): string {
    if (!credit) return '';
    const ariaLabel = `Photo by ${credit}`;
    const icon = creditUrl
        ? `<a href="${escapeHtml(creditUrl)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(ariaLabel)}">${CAMERA_SVG}</a>`
        : `<span aria-hidden="true">${CAMERA_SVG}</span>`;
    return `<span class="photo-credit">${icon} ${escapeHtml(credit)}</span>`;
}

function renderImageMeta(credit: string, creditUrl: string, caption: string): string {
    if (!credit && !caption) return '';
    const parts: string[] = [];
    if (credit) parts.push(renderPhotoCredit(credit, creditUrl));
    if (caption) parts.push(`<span class="image-caption">${escapeHtml(caption)}</span>`);
    return `<figcaption class="image-meta">${parts.join('')}</figcaption>`;
}

/**
 * Wrap an image in <picture> with AVIF + WebP responsive sources when the
 * src is a CDN URL with width variants on disk. Falls back to plain <img>
 * for non-CDN URLs or unsupported extensions.
 *
 * `extraAttrs` is a pre-escaped attribute string appended to <img> (e.g.
 * `data-gallery` or `class="..."`). `sizes` is the layout-dependent hint.
 */
// Per-call map of CDN URL → server-confirmed variant widths from the
// Media row's metadata.variants. Set on each invocation by tiptapToEditorialHtml
// via the second argument. Empty/missing = fall back to plain <img>.
let CURRENT_MEDIA_VARIANTS: Record<string, number[]> = {};

function renderResponsiveImage(src: string, alt: string, extraAttrs: string, sizes: string): string {
    const safeAlt = escapeHtml(alt);
    const safeSrc = escapeHtml(src);
    const attrs = extraAttrs ? ` ${extraAttrs}` : '';
    if (!src.startsWith('https://cdnweb.luxrallytravel.com/')) {
        return `<img src="${safeSrc}" alt="${safeAlt}"${attrs} />`;
    }
    const dot = src.lastIndexOf('.');
    if (dot < 0) {
        return `<img src="${safeSrc}" alt="${safeAlt}"${attrs} />`;
    }
    // Use ONLY widths the server confirmed exist on S3. No server data → plain <img>
    // fallback (better than emitting <source> URLs that might 403 and leave gray).
    const widths = CURRENT_MEDIA_VARIANTS[src];
    if (!widths || widths.length === 0) {
        return `<img src="${safeSrc}" alt="${safeAlt}"${attrs} />`;
    }
    const base = src.slice(0, dot);
    const avifSrcset = widths.map((w) => `${escapeHtml(`${base}-w${w}.avif`)} ${w}w`).join(', ');
    const webpSrcset = widths.map((w) => `${escapeHtml(`${base}-w${w}.webp`)} ${w}w`).join(', ');
    const safeSizes = escapeHtml(sizes);
    return `<picture>` +
        `<source type="image/avif" srcset="${avifSrcset}" sizes="${safeSizes}" />` +
        `<source type="image/webp" srcset="${webpSrcset}" sizes="${safeSizes}" />` +
        `<img src="${safeSrc}" alt="${safeAlt}"${attrs} />` +
        `</picture>`;
}

/**
 * Convert TipTap JSON document to editorial HTML matching the guide page design system.
 *
 * `mediaVariants` is an optional map of CDN URL → server-confirmed variant
 * widths (from each Media row's metadata.variants). When provided, the
 * renderer emits <picture> with srcset entries for ONLY those widths. When
 * omitted (or empty for a given URL), body images render as plain <img>.
 */
export function tiptapToEditorialHtml(
    doc: Record<string, unknown> | null,
    mediaVariants: Record<string, number[]> = {},
): string {
    if (!doc || !doc.content) return '';

    CURRENT_MEDIA_VARIANTS = mediaVariants;
    try {
        const content = doc.content as any[];
        return content.map(renderNode).join('\n');
    } finally {
        CURRENT_MEDIA_VARIANTS = {};
    }
}

function renderNode(node: any): string {
    if (!node) return '';

    switch (node.type) {
        case 'paragraph': {
            const pText = renderInline(node.content);
            // Filter out empty paragraphs and orphaned slash characters from slash commands
            if (!pText || pText.trim() === '/' || pText.trim() === '') return '';
            return `<p>${pText}</p>`;
        }

        case 'heading': {
            const level = node.attrs?.level || 2;
            return `<h${level}>${renderInline(node.content)}</h${level}>`;
        }

        case 'bulletList':
            return `<ul>${(node.content || []).map((li: any) => `<li>${renderInline(li.content?.[0]?.content)}</li>`).join('')}</ul>`;

        case 'orderedList':
            return `<ol>${(node.content || []).map((li: any) => `<li>${renderInline(li.content?.[0]?.content)}</li>`).join('')}</ol>`;

        case 'blockquote':
            return `<blockquote>${(node.content || []).map(renderNode).join('')}</blockquote>`;

        case 'codeBlock':
            return `<pre><code>${escapeHtml(getTextContent(node))}</code></pre>`;

        case 'horizontalRule':
            return '<hr />';

        case 'editorialPullQuote': {
            const quote = node.attrs?.quote || '';
            const attribution = node.attrs?.attribution || '';
            return `<div class="editorial-pull-quote">
                <blockquote>${escapeHtml(quote)}</blockquote>
                ${attribution ? `<cite>\u2014 ${escapeHtml(attribution)}</cite>` : ''}
            </div>`;
        }

        case 'editorialImage': {
            const imgSrc = node.attrs?.src || '';
            const imgAlt = node.attrs?.alt || '';
            const imgCaption = node.attrs?.caption || '';
            const imgCredit = node.attrs?.credit || '';
            const imgCreditUrl = node.attrs?.creditUrl || '';
            const imgLayout = node.attrs?.layout || 'full-bleed';
            if (!imgSrc) return '';
            const imgClass = imgLayout === 'standard' ? 'editorial-standard-image' : 'editorial-full-bleed';
            // Full-bleed → 100vw; standard sits inside max-w-3xl (~768px) prose column.
            const sizes = imgLayout === 'standard' ? '(max-width: 768px) 100vw, 768px' : '100vw';
            return `<figure class="${imgClass}">
                ${renderResponsiveImage(imgSrc, imgAlt, '', sizes)}
                ${renderImageMeta(imgCredit, imgCreditUrl, imgCaption)}
            </figure>`;
        }

        case 'editorialInfoBox': {
            const label = node.attrs?.label || 'Note';
            const infoContent = node.attrs?.content || '';
            return `<div class="editorial-info-box">
                <div class="info-label">${escapeHtml(label)}</div>
                <p>${escapeHtml(infoContent)}</p>
            </div>`;
        }

        case 'editorialCta': {
            const ctaImg = node.attrs?.imageUrl || '';
            const ctaKicker = node.attrs?.kicker || '';
            const ctaTitle = node.attrs?.title || '';
            const ctaDesc = node.attrs?.description || '';
            const ctaBtnText = node.attrs?.buttonText || 'Learn More';
            return `<div class="editorial-cta-card">
                ${ctaImg ? `<img src="${escapeHtml(ctaImg)}" alt="" class="cta-image" />` : ''}
                <div class="cta-content">
                    ${ctaKicker ? `<div class="cta-kicker">${escapeHtml(ctaKicker)}</div>` : ''}
                    <div class="cta-title">${escapeHtml(ctaTitle)}</div>
                    ${ctaDesc ? `<div class="cta-description">${escapeHtml(ctaDesc)}</div>` : ''}
                    <button type="button" class="cta-button" data-inquiry-trigger>${escapeHtml(ctaBtnText)}</button>
                </div>
            </div>`;
        }

        case 'editorialDivider': {
            const kicker = node.attrs?.kicker || '';
            return `<div class="editorial-divider">
                ${kicker ? `<span class="kicker">${escapeHtml(kicker)}</span>` : ''}
            </div>`;
        }

        case 'editorialFaq': {
            const items = parseJsonAttr(node.attrs?.items);
            const faqTitle = node.attrs?.sectionTitle || 'FAQ';
            return `<div class="editorial-faq-section">
                <h3>${escapeHtml(faqTitle)}</h3>
                ${items
                    .map(
                        (item: any) => `
                    <details class="editorial-faq-item">
                        <summary>${escapeHtml(item.question || '')}</summary>
                        <p>${escapeHtml(item.answer || '')}</p>
                    </details>
                `,
                    )
                    .join('')}
            </div>`;
        }

        case 'editorialTable': {
            // columns: string[] (first is the row label); rows: string[][] — both
            // JSON-string attrs. Malformed shapes render nothing; rows are padded /
            // trimmed to the column count so the grid never goes ragged.
            // parseJsonAttr hands back whatever JSON.parse produced — guard the shape
            // before mapping so a stray string/object attr can't throw mid-render.
            const rawColumns: unknown = parseJsonAttr(node.attrs?.columns);
            const rawRows: unknown = parseJsonAttr(node.attrs?.rows);
            if (!Array.isArray(rawColumns) || !Array.isArray(rawRows) || rawColumns.length === 0 || rawRows.length === 0) return '';
            const columns: string[] = rawColumns.map((c: unknown) => (c == null ? '' : String(c)));
            const rows: string[][] = rawRows.map((row: unknown) => {
                const cells: string[] = Array.isArray(row) ? row.map((c: unknown) => (c == null ? '' : String(c))) : [];
                while (cells.length < columns.length) cells.push('');
                return cells.slice(0, columns.length);
            });
            const hasContent = columns.some((c: string) => c.trim() !== '') || rows.some((row) => row.some((c) => c.trim() !== ''));
            if (!hasContent) return '';
            const tableTitle = node.attrs?.title || '';
            const tableNote = node.attrs?.note || '';
            const cell = (text: string) => (text.trim() ? escapeHtml(text) : '<span class="editorial-table-empty">—</span>');
            return `<div class="editorial-table">
                ${tableTitle ? `<p class="editorial-table-title">${escapeHtml(tableTitle)}</p>` : ''}
                <div class="editorial-table-scroll">
                    <table>
                        <thead>
                            <tr>${columns.map((c: string) => `<th>${cell(c)}</th>`).join('')}</tr>
                        </thead>
                        <tbody>
                            ${rows
                                .map(
                                    (row) =>
                                        `<tr>${row.map((c, i) => (i === 0 ? `<th scope="row">${cell(c)}</th>` : `<td>${cell(c)}</td>`)).join('')}</tr>`,
                                )
                                .join('')}
                        </tbody>
                    </table>
                </div>
                ${tableNote ? `<p class="editorial-table-note">${escapeHtml(tableNote)}</p>` : ''}
            </div>`;
        }

        case 'editorialGallery': {
            const images = parseJsonAttr(node.attrs?.images);
            if (images.length === 0) return '';

            const captionMode = node.attrs?.captionMode === 'shared' ? 'shared' : 'individual';
            const sourceMode = node.attrs?.sourceMode === 'shared' ? 'shared' : 'individual';
            const sharedCaption = node.attrs?.sharedCaption || '';
            const sharedCredit = node.attrs?.sharedCredit || '';
            const sharedCreditUrl = node.attrs?.sharedCreditUrl || '';

            // Gallery sits inside max-w-3xl (~768px). With multiple images per row,
            // each takes ~50% of the column on desktop, full width on mobile.
            const gallerySizes = images.length > 1 ? '(max-width: 768px) 100vw, 50vw' : '(max-width: 768px) 100vw, 768px';
            const figures = images
                .map((img: any) => {
                    const credit = sourceMode === 'individual' ? img.credit || '' : '';
                    const creditUrl = sourceMode === 'individual' ? img.creditUrl || '' : '';
                    const caption = captionMode === 'individual' ? img.caption || '' : '';
                    return `
                    <figure>
                        ${renderResponsiveImage(img.src || '', img.alt || '', 'data-gallery', gallerySizes)}
                        ${renderImageMeta(credit, creditUrl, caption)}
                    </figure>
                `;
                })
                .join('');

            const sharedCreditHtml =
                sourceMode === 'shared' && sharedCredit ? renderPhotoCredit(`Gallery images from ${sharedCredit}`, sharedCreditUrl) : '';
            const sharedCaptionHtml =
                captionMode === 'shared' && sharedCaption ? `<span class="image-caption">${escapeHtml(sharedCaption)}</span>` : '';
            const sharedMeta =
                sharedCreditHtml || sharedCaptionHtml
                    ? `<figcaption class="image-meta editorial-gallery-shared-meta">${sharedCreditHtml}${sharedCaptionHtml}</figcaption>`
                    : '';

            return `<div class="editorial-gallery" data-count="${images.length}">
                ${figures}
                ${sharedMeta}
            </div>`;
        }

        case 'editorialVideo': {
            const videoSrc = node.attrs?.src || '';
            const videoCaption = node.attrs?.caption || '';
            const videoLayout = node.attrs?.layout || 'full-bleed';
            if (!videoSrc) return '';

            let videoHtml = '';
            if (videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be')) {
                const videoId = extractYoutubeId(videoSrc);
                videoHtml = `<iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen style="width: 100%; aspect-ratio: 16/9;"></iframe>`;
            } else if (videoSrc.includes('vimeo.com')) {
                const vimeoId = videoSrc.split('/').pop();
                videoHtml = `<iframe src="https://player.vimeo.com/video/${vimeoId}" frameborder="0" allowfullscreen style="width: 100%; aspect-ratio: 16/9;"></iframe>`;
            } else {
                videoHtml = `<video src="${escapeHtml(videoSrc)}" controls style="width: 100%; aspect-ratio: 16/9;"></video>`;
            }

            const videoClass = videoLayout === 'standard' ? 'editorial-standard-image editorial-video' : 'editorial-full-bleed editorial-video';
            return `<figure class="${videoClass}">
                ${videoHtml}
                ${videoCaption ? `<figcaption class="image-meta"><span class="image-caption">${escapeHtml(videoCaption)}</span></figcaption>` : ''}
            </figure>`;
        }

        case 'image': {
            const src = node.attrs?.src || '';
            if (!src) return '';
            return `<figure class="editorial-full-bleed">${renderResponsiveImage(src, node.attrs?.alt || '', '', '100vw')}</figure>`;
        }

        case 'youtube': {
            const ytSrc = node.attrs?.src || '';
            if (!ytSrc) return '';
            return `<figure class="editorial-full-bleed"><iframe src="${escapeHtml(ytSrc)}" frameborder="0" allowfullscreen style="width: 100%; aspect-ratio: 16/9;"></iframe></figure>`;
        }

        default:
            // Unknown node types — try to render content
            if (node.content) {
                return (node.content as any[]).map(renderNode).join('');
            }
            return '';
    }
}

function renderInline(content: any[] | undefined): string {
    if (!content) return '';
    return content
        .map((node: any) => {
            if (node.type === 'text') {
                let text = escapeHtml(node.text || '');
                if (node.marks) {
                    for (const mark of node.marks) {
                        switch (mark.type) {
                            case 'bold':
                                text = `<strong>${text}</strong>`;
                                break;
                            case 'italic':
                                text = `<em>${text}</em>`;
                                break;
                            case 'underline':
                                text = `<u>${text}</u>`;
                                break;
                            case 'link':
                                text = `<a href="${escapeHtml(mark.attrs?.href || '#')}">${text}</a>`;
                                break;
                            case 'code':
                                text = `<code>${text}</code>`;
                                break;
                        }
                    }
                }
                return text;
            }
            if (node.type === 'hardBreak') return '<br />';
            return '';
        })
        .join('');
}

function getTextContent(node: any): string {
    if (!node.content) return node.text || '';
    return node.content.map(getTextContent).join('');
}

function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function parseJsonAttr(val: any): any[] {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
        try {
            return JSON.parse(val);
        } catch {
            return [];
        }
    }
    return [];
}

function extractYoutubeId(url: string): string {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match?.[1] || '';
}
